import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import {useField} from "formik";
import PropTypes from "prop-types";

import {COLOR_SCHEME} from "../../utilities/colorSheme";
import {decodeCurrencySymbol} from "../../utilities/currencyFormat";
import {toaster} from "../../utilities/hooks/toaster";
import {calcDynamicPrecision, numberFormat} from "../../utilities/moneyFormat";
import QuestionItem from "../../utilities/QuestionItem";
import ToolTip from "../../utilities/ToolTip";
import useRoutePermissions, {
  UI_PERMISSION_TYPE,
} from "../adminForms/Ba3aSettings/tabs/PositionsSettings/useRoutePermissions";

const NumberInput = ({
  label,
  width = "67%",
  height,
  className = "",
  generalClassName = "",
  question = null, //подсказка в виде знака вопроса рядом с инпутом
  autolabel,
  readOnly,
  labelStyles,
  labelWrap,
  placeholder,
  inputClassName,
  questionItemClassName = "",
  handleInputChange = () => {},
  noFormat = false, //без форматирования
  formatOnlyView = false, //format only view, not formik value
  disabled = false,
  arrows = false, //дефолтные стрелки вверх-вниз (увеличить-уменьшить) числового инпута
  minusValues = false,
  setref = null,
  maxNumberLength, //макс кол-во символов в введенном числе
  minNumber, //мин число, включая его
  maxNumber, //макс число, включая его
  showValueRestrictions = true, //показывать ли ограничения по мин/макс числу в подсказке
  maxNumberExcl, //макс число, не включая его
  onBlur = () => {},
  required,
  noInitialNullValue = false, //0.00 не показывается, вместо - пустая строка
  tooltipMessage = "",
  bottomTooltip = false,
  precision = 2,
  dynamicPrecision = true, //if number has more zeros after comma then precision, show first digit after zeros (for example 0.0004001 with precision 2 will be shown as 0.0004, not 0.00)
  simpleZero = false, // '0', not '0.00000'
  areExcessiveZerosRemoved = true,
  isMoney = false,
  moneySign = "€",
  moneyShortName,
  grow = false,
  showPercentageSign = false, // % after input
  accentText = false, //show input (not label) in alert color
  noFunctional = false,
  isRequiredAccented = true,
  uiPermissionType = UI_PERMISSION_TYPE.WRITABLE,

  ...props
}) => {
  const {isDisabledByRoutePermission} = useRoutePermissions({uiPermissionType});

  const {t} = useTranslation();
  const [field, meta, helpers] = useField(props);
  // const [value, setValue] = useState("");

  const [showValue, setShowValue] = useState(""); //text value, that is formatted and shown in fild (if formatOnlyView - only showValue is formatted, but formik field.value is not)

  const options = {
    decimal: ".",
    thousand: "'",
    precision: calcDynamicPrecision({value: field.value, defaultPrecision: precision, dynamicPrecision, simpleZero}),
    format: "%s%v",
  };

  // useEffect(() => {
  //   if (formatOnlyView) {
  //     setShowValue(accounting.formatNumber(value, options));
  //   }
  // }, [value]);

  const inputRef = useRef(null);

  const setInputRef = ref => {
    if (ref) {
      inputRef.current = ref;
      if (setref) setref(ref);
    }
  };

  const {setValue: setRawValue} = helpers;

  //Чтобы число округлялось до нужного кол-ва знаков после запятой, а не просто обрезалось
  const precisionRound = ({precision, value}) => {
    const number = Number.parseFloat(value);

    return Math.round(number * Math.pow(10, precision)) / Math.pow(10, precision);
  };

  useEffect(() => {
    if (field.value === "" || (!field.value && noInitialNullValue)) {
      setShowValue("");
    } else {
      if (noFormat) {
        setShowValue(field.value);
      } else if (formatOnlyView) {
        setShowValue(numberFormat(field.value, options.precision, true, areExcessiveZerosRemoved));
      } else {
        setShowValue(
          numberFormat(
            precisionRound({precision, value: field.value}),
            options.precision,
            true,
            areExcessiveZerosRemoved,
          ),
        );
        field.value && setRawValue(Number.parseFloat(field.value).toFixed(precision));
      }
    }
  }, [field.value]);

  const handleFocus = () => {
    inputRef?.current.select();
  };

  const handleChange = e => {
    const changedValue = e.target.value;

    const dotIndex = changedValue.indexOf(".");
    const symbolsAfterDot = dotIndex && dotIndex !== -1 ? changedValue.slice(dotIndex + 1)?.length : 0;
    const numericValue = Number.parseFloat(changedValue);

    //checking for min-max and length
    if (
      (maxNumberLength && numericValue.length > maxNumberLength) ||
      // (minNumber && minNumber < numericValue) ||
      (maxNumber && numericValue > maxNumber) ||
      (maxNumberExcl && numericValue >= maxNumberExcl) ||
      symbolsAfterDot > precision
    )
      return;

    //remove not numbers, commas and minus (if noMinusValues)
    const formattedValue = (
      !minusValues
        ? changedValue.replace(/[^0-9.,]/g, "")
        : changedValue
        ? changedValue[0]?.replace(/[^0-9.,-]/g, "") + changedValue.slice(1).replace(/[^0-9.,]/g, "")
        : ""
    ).replaceAll(",", ".");

    handleInputChange(e);
    setShowValue(formattedValue);
  };

  const handleBlur = e => {
    if (readOnly || disabled || noFunctional || isDisabledByRoutePermission) return;

    const changedValue = e.target.value;
    const numericValue = Number.parseFloat(changedValue);

    if (minNumber && numericValue < minNumber) {
      toaster.error(`${t("Number input must be not less than")} ${minNumber}`);
      setRawValue("");
      return;
    }

    setRawValue(showValue);
    formatOnlyView && setShowValue(numberFormat(e.target.value, options.precision, true, areExcessiveZerosRemoved));
    onBlur(e);
  };

  const labelText = label ? t(label) : "";
  const isRequiredMarkHighlighted =
    !field.value && !disabled && !noFunctional && !readOnly && !isDisabledByRoutePermission;

  const isValueRestrictionsTooltipShown =
    !disabled &&
    !readOnly &&
    !noFunctional &&
    !isDisabledByRoutePermission &&
    showValueRestrictions &&
    (minNumber || maxNumber || maxNumberExcl);
  const valueRestrictionsText = `${minNumber !== undefined ? t("Min. sum") + ": " + minNumber + ". " : ""}${
    maxNumber !== undefined ? t("Max. sum") + ": " + maxNumber + ". " : ""
  }${maxNumberExcl !== undefined ? t("Max sum excl.") + ": " + maxNumberExcl + ". " : ""}`;

  const generalTooltipTitle = noFunctional
    ? "No functional"
    : isDisabledByRoutePermission && !readOnly && !disabled
    ? "Your position has no permission for this action"
    : typeof tooltipMessage === "string"
    ? `${t(tooltipMessage)}${isValueRestrictionsTooltipShown ? "(" + valueRestrictionsText + ")" : ""}`
    : tooltipMessage;

  return (
    <ToolTip title={generalTooltipTitle} bottom={bottomTooltip} noTranslation={isValueRestrictionsTooltipShown}>
      <div className={`project_input ${labelWrap ? "" : "labelWrap"} ${generalClassName}`}>
        {label && (
          <label htmlFor={props.name} style={labelStyles} className={autolabel ? "autolabel" : ""}>
            <span>
              {labelText}
              {required && (
                <span className={isRequiredAccented && isRequiredMarkHighlighted ? "required-asterisk" : ""}>*</span>
              )}
              :
            </span>

            {/* {`${t(label)}${required ? "*:" : ":"}`} */}
          </label>
        )}
        <div
          className=""
          style={{
            width: width,
            position: "relative",
            height: height,
            ...(grow ? {flex: "1 1 auto", minWidth: 0} : {}),
          }}
        >
          <div className={className}>
            <div className="row aibl">
              <span className={clsx(isMoney && !moneyShortName && moneySign && "pl5")}>
                {isMoney && !moneyShortName && moneySign ? decodeCurrencySymbol(moneySign) : ""}
              </span>
              <input
                readOnly={readOnly || noFunctional}
                autoComplete="off"
                value={showValue ?? ""}
                onBlur={handleBlur}
                onChange={handleChange}
                ref={setInputRef}
                onFocus={() => {
                  !readOnly && !noFunctional && !isDisabledByRoutePermission && handleFocus();
                }}
                className={`${inputClassName} ${!arrows ? "inputNoArrows" : ""}`}
                placeholder={placeholder}
                style={{
                  height: height,
                  ...(readOnly || disabled || noFunctional || isDisabledByRoutePermission
                    ? {cursor: "default"}
                    : {background: "white"}),
                  ...(accentText ? {color: COLOR_SCHEME.ALERT} : {}),
                }}
                disabled={isDisabledByRoutePermission || disabled}
              />
              {showPercentageSign && "%"}
              {isMoney && moneyShortName && <span className="pl5"> {moneyShortName}</span>}
            </div>
          </div>
          {meta.touched && meta.error ? <div className="error">{meta.error}</div> : null}
        </div>

        {question ? <QuestionItem title={question || valueRestrictionsText} className={questionItemClassName} /> : null}
      </div>
    </ToolTip>
  );
};

NumberInput.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.node]),
  width: PropTypes.string,
  props: PropTypes.array,
  className: PropTypes.string,
  question: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  autolabel: PropTypes.bool,
  readOnly: PropTypes.bool,
  noFunctional: PropTypes.bool,
  handleInputChange: PropTypes.func,
  disabled: PropTypes.bool,
  noFormat: PropTypes.bool,
  grow: PropTypes.bool,
  showPercentageSign: PropTypes.bool,
};

export default NumberInput;
